import { definePlatformSetting, } from '@crest/platform-contracts/settings';
import { z } from 'zod';
import { tasksTaskTemplateSchema } from '../contracts/tasks-focus.contract.js';
const tasksStatusLabelsSchema = z.strictObject({
    open: z.string().trim().min(1).max(60),
    in_progress: z.string().trim().min(1).max(60),
    blocked: z.string().trim().min(1).max(60),
    done: z.string().trim().min(1).max(60),
    cancelled: z.string().trim().min(1).max(60),
});
const tasksPriorityLabelsSchema = z.strictObject({
    low: z.string().trim().min(1).max(60),
    normal: z.string().trim().min(1).max(60),
    high: z.string().trim().min(1).max(60),
    urgent: z.string().trim().min(1).max(60),
});
const taskEditorCopyDefaults = {
    viewAction: 'View',
    discardEditAction: 'Cancel',
    returnToProjectAction: 'Back to Project',
    noChangesLabel: 'No task fields have changed.',
    validationError: 'Check the task fields before saving.',
    changedError: 'This task changed or is no longer editable. Reload it before editing again.',
    saveUncertainError: 'The save outcome is unknown. Retry the same save to check it; do not submit a different change.',
    readbackError: 'The save was acknowledged, but its current values could not be verified. Retry readback.',
    retrySaveAction: 'Retry same save',
    retryReadbackAction: 'Retry readback',
    reloadAction: 'Reload task',
    retainedSelectionLabel: 'Retained selection (not in the current directory)',
    localTimeHelp: 'Dates use this browser’s local time. Unchanged dates retain their original precision.',
    savedLabel: 'Task saved and verified.',
};
export const tasksWorkQueueCopyValueSchema = z.strictObject({
    viewAction: z.string().trim().min(1).max(300).default(taskEditorCopyDefaults.viewAction),
    discardEditAction: z.string().trim().min(1).max(300).default(taskEditorCopyDefaults.discardEditAction),
    returnToProjectAction: z.string().trim().min(1).max(300).default(taskEditorCopyDefaults.returnToProjectAction),
    noChangesLabel: z.string().trim().min(1).max(300).default(taskEditorCopyDefaults.noChangesLabel),
    validationError: z.string().trim().min(1).max(300).default(taskEditorCopyDefaults.validationError),
    changedError: z.string().trim().min(1).max(300).default(taskEditorCopyDefaults.changedError),
    saveUncertainError: z.string().trim().min(1).max(300).default(taskEditorCopyDefaults.saveUncertainError),
    readbackError: z.string().trim().min(1).max(300).default(taskEditorCopyDefaults.readbackError),
    retrySaveAction: z.string().trim().min(1).max(300).default(taskEditorCopyDefaults.retrySaveAction),
    retryReadbackAction: z.string().trim().min(1).max(300).default(taskEditorCopyDefaults.retryReadbackAction),
    reloadAction: z.string().trim().min(1).max(300).default(taskEditorCopyDefaults.reloadAction),
    retainedSelectionLabel: z.string().trim().min(1).max(300).default(taskEditorCopyDefaults.retainedSelectionLabel),
    localTimeHelp: z.string().trim().min(1).max(300).default(taskEditorCopyDefaults.localTimeHelp),
    savedLabel: z.string().trim().min(1).max(300).default(taskEditorCopyDefaults.savedLabel),
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(300),
    mineLabel: z.string().trim().min(1).max(60),
    allLabel: z.string().trim().min(1).max(60),
    createAction: z.string().trim().min(1).max(80),
    editAction: z.string().trim().min(1).max(80),
    saveAction: z.string().trim().min(1).max(80),
    resetAction: z.string().trim().min(1).max(80),
    cancelAction: z.string().trim().min(1).max(80),
    reopenAction: z.string().trim().min(1).max(80),
    signOffAction: z.string().trim().min(1).max(80),
    completeAction: z.string().trim().min(1).max(80),
    addCommentAction: z.string().trim().min(1).max(80),
    commentsLabel: z.string().trim().min(1).max(80),
    hideCommentsLabel: z.string().trim().min(1).max(80),
    titleLabel: z.string().trim().min(1).max(80),
    descriptionLabel: z.string().trim().min(1).max(80),
    projectLabel: z.string().trim().min(1).max(80),
    personalTaskLabel: z.string().trim().min(1).max(100),
    assigneesLabel: z.string().trim().min(1).max(80),
    statusLabel: z.string().trim().min(1).max(80),
    priorityLabel: z.string().trim().min(1).max(80),
    dueAtLabel: z.string().trim().min(1).max(80),
    completionRuleLabel: z.string().trim().min(1).max(80),
    oneCompletesForAllLabel: z.string().trim().min(1).max(120),
    allAssigneesSignOffLabel: z.string().trim().min(1).max(120),
    blockedReasonLabel: z.string().trim().min(1).max(80),
    blockedDetailLabel: z.string().trim().min(1).max(80),
    waitingOnLabel: z.string().trim().min(1).max(80),
    nextChaseAtLabel: z.string().trim().min(1).max(80),
    estimatedMinutesLabel: z.string().trim().min(1).max(80),
    allStatusesLabel: z.string().trim().min(1).max(80),
    noAssigneesLabel: z.string().trim().min(1).max(120),
    completionProgressLabel: z.string().trim().min(1).max(120),
    actionReasonLabel: z.string().trim().min(1).max(80),
    actionReasonPlaceholder: z.string().trim().min(1).max(160),
    commentPlaceholder: z.string().trim().min(1).max(160),
    savingLabel: z.string().trim().min(1).max(120),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(300),
    loadingLabel: z.string().trim().min(1).max(120),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    statusLabels: tasksStatusLabelsSchema,
    priorityLabels: tasksPriorityLabelsSchema,
});
export const tasksWorkQueueCopySetting = definePlatformSetting({
    definition: {
        key: 'tasks.copy.work-queue',
        moduleId: 'tasks',
        title: 'Tasks work-queue copy',
        helpText: 'Controls tenant-editable labels in Tasks work queues and editors.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            ...taskEditorCopyDefaults,
            navigationLabel: 'Tasks',
            title: 'Tasks',
            description: 'Personal and Project work, assignments, sign-offs, and working notes.',
            mineLabel: 'Mine',
            allLabel: 'All',
            createAction: 'New task',
            editAction: 'Edit task',
            saveAction: 'Save task',
            resetAction: 'Clear form',
            cancelAction: 'Cancel task',
            reopenAction: 'Reopen task',
            signOffAction: 'Sign off',
            completeAction: 'Complete task',
            addCommentAction: 'Add comment',
            commentsLabel: 'Comments',
            hideCommentsLabel: 'Hide comments',
            titleLabel: 'Title',
            descriptionLabel: 'Description',
            projectLabel: 'Project',
            personalTaskLabel: 'Personal / no Project',
            assigneesLabel: 'Assignees',
            statusLabel: 'Status',
            priorityLabel: 'Priority',
            dueAtLabel: 'Due',
            completionRuleLabel: 'Completion rule',
            oneCompletesForAllLabel: 'One assignee completes for everyone',
            allAssigneesSignOffLabel: 'Every assignee must sign off',
            blockedReasonLabel: 'Blocked reason',
            blockedDetailLabel: 'Blocked details',
            waitingOnLabel: 'Waiting on',
            nextChaseAtLabel: 'Next follow-up',
            estimatedMinutesLabel: 'Estimated minutes',
            allStatusesLabel: 'All statuses',
            noAssigneesLabel: 'No assignees',
            completionProgressLabel: 'Sign-offs',
            actionReasonLabel: 'Reason',
            actionReasonPlaceholder: 'Explain why this task is being changed',
            commentPlaceholder: 'Add a working note',
            savingLabel: 'Saving…',
            emptyTitle: 'No tasks in this queue',
            emptyDescription: 'Tasks matching the current scope and status will appear here.',
            loadingLabel: 'Loading tasks…',
            loadError: 'Tasks could not be loaded.',
            mutationError: 'The task could not be saved. Refresh and try again.',
            statusLabels: {
                open: 'Open', in_progress: 'In progress', blocked: 'Blocked', done: 'Done', cancelled: 'Cancelled',
            },
            priorityLabels: { low: 'Low', normal: 'Normal', high: 'High', urgent: 'Urgent' },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: tasksWorkQueueCopyValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => {
                const source = typeof value === 'object' && value !== null && !Array.isArray(value)
                    ? value
                    : {};
                return { ...taskEditorCopyDefaults, ...source };
            },
        }],
});
export const tasksWorkQueueDisplayValueSchema = z.strictObject({
    defaultScope: z.enum(['mine', 'all']),
    defaultStatus: z.enum(['open', 'in_progress', 'blocked', 'done', 'cancelled', 'all']),
    pageSize: z.union([z.literal(25), z.literal(50), z.literal(100)]),
    density: z.enum(['comfortable', 'compact']),
    showDescriptions: z.boolean(),
    commentsDefaultOpen: z.boolean(),
});
export const tasksWorkQueueDisplaySetting = definePlatformSetting({
    definition: {
        key: 'tasks.work-queue.display',
        moduleId: 'tasks',
        title: 'Tasks work-queue display',
        helpText: 'Controls the default queue scope, density, page size, and card detail.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            defaultScope: 'mine',
            defaultStatus: 'open',
            pageSize: 50,
            density: 'comfortable',
            showDescriptions: true,
            commentsDefaultOpen: false,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: tasksWorkQueueDisplayValueSchema,
});
function isIanaTimeZone(value) {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value }).format(new Date(0));
        return true;
    }
    catch {
        return false;
    }
}
export const tasksFocusConfigurationValueSchema = z.strictObject({
    enabled: z.boolean(),
    timeZone: z.string().trim().min(1).max(100).refine(isIanaTimeZone, 'Expected an IANA time zone.'),
    defaultWindowType: z.enum(['daily', 'weekly']),
    weekStartsOnIsoDay: z.number().int().min(1).max(7),
    myDayDueSoonDays: z.number().int().min(1).max(30),
    myDayMaxItemsPerGroup: z.union([z.literal(3), z.literal(5), z.literal(10)]),
    projectPanelEnabled: z.boolean(),
    projectPanelLimit: z.union([z.literal(5), z.literal(10), z.literal(20)]),
    templates: z.array(tasksTaskTemplateSchema).max(30).superRefine((templates, context) => {
        if (new Set(templates.map(({ id }) => id)).size !== templates.length) {
            context.addIssue({ code: 'custom', message: 'Task template IDs must be unique.' });
        }
    }),
});
function migrateTasksFocusConfigurationV1(value) {
    const source = typeof value === 'object' && value !== null && !Array.isArray(value)
        ? value
        : {};
    const templates = Array.isArray(source.templates) ? source.templates : [];
    return {
        ...source,
        templates: templates.map((template) => (typeof template === 'object' && template !== null && !Array.isArray(template)
            ? { ...template, checklistItems: [], links: [] }
            : template)),
    };
}
function migrateTasksFocusConfigurationV2(value) {
    const source = typeof value === 'object' && value !== null && !Array.isArray(value)
        ? value
        : {};
    return { ...source, myDayMaxItemsPerGroup: 5 };
}
export const tasksFocusConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'tasks.focus.configuration',
        moduleId: 'tasks',
        title: 'Tasks focus and templates',
        helpText: 'Controls Focus planning dates, reusable task templates, My Day horizon, and the Project task panel.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 3,
        codedFallback: {
            enabled: true,
            timeZone: 'America/Indiana/Indianapolis',
            defaultWindowType: 'daily',
            weekStartsOnIsoDay: 1,
            myDayDueSoonDays: 7,
            myDayMaxItemsPerGroup: 5,
            projectPanelEnabled: true,
            projectPanelLimit: 10,
            templates: [
                {
                    id: 'customer-follow-up',
                    label: 'Customer follow-up',
                    title: 'Follow up with customer',
                    description: null,
                    priority: 'normal',
                    defaultLane: 'today',
                    estimatedMinutes: 15,
                    checklistItems: [],
                    links: [],
                },
                {
                    id: 'project-status-review',
                    label: 'Project status review',
                    title: 'Review Project status and next action',
                    description: null,
                    priority: 'high',
                    defaultLane: 'today',
                    estimatedMinutes: 20,
                    checklistItems: [],
                    links: [],
                },
                {
                    id: 'estimate-follow-up',
                    label: 'Estimate follow-up',
                    title: 'Follow up on estimate',
                    description: null,
                    priority: 'normal',
                    defaultLane: 'waiting',
                    estimatedMinutes: 10,
                    checklistItems: [],
                    links: [],
                },
            ],
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'replace',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: tasksFocusConfigurationValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: migrateTasksFocusConfigurationV1,
        }, {
            fromVersion: 2,
            toVersion: 3,
            migrate: migrateTasksFocusConfigurationV2,
        }],
});
export const tasksDetailCopyValueSchema = z.strictObject({
    backToTasksAction: z.string().trim().min(1).max(100),
    descriptionLabel: z.string().trim().min(1).max(100),
    projectLabel: z.string().trim().min(1).max(100),
    assigneesLabel: z.string().trim().min(1).max(100),
    statusLabel: z.string().trim().min(1).max(100),
    priorityLabel: z.string().trim().min(1).max(100),
    dueLabel: z.string().trim().min(1).max(100),
    noDueLabel: z.string().trim().min(1).max(100),
    commentsTitle: z.string().trim().min(1).max(100),
    commentPlaceholder: z.string().trim().min(1).max(180),
    addCommentAction: z.string().trim().min(1).max(100),
    checklistTitle: z.string().trim().min(1).max(100),
    checklistProgressLabel: z.string().trim().min(1).max(160),
    checklistEmpty: z.string().trim().min(1).max(240),
    checklistItemLabel: z.string().trim().min(1).max(100),
    checklistRequiredLabel: z.string().trim().min(1).max(100),
    addChecklistAction: z.string().trim().min(1).max(100),
    completeChecklistAction: z.string().trim().min(1).max(100),
    reopenChecklistAction: z.string().trim().min(1).max(100),
    removeChecklistAction: z.string().trim().min(1).max(100),
    linksTitle: z.string().trim().min(1).max(100),
    linksEmpty: z.string().trim().min(1).max(240),
    linkLabel: z.string().trim().min(1).max(100),
    linkHrefLabel: z.string().trim().min(1).max(100),
    addLinkAction: z.string().trim().min(1).max(100),
    removeLinkAction: z.string().trim().min(1).max(100),
    templatesTitle: z.string().trim().min(1).max(100),
    templateLabel: z.string().trim().min(1).max(100),
    applyTemplateAction: z.string().trim().min(1).max(100),
    noTemplatesLabel: z.string().trim().min(1).max(180),
    templateHistoryTitle: z.string().trim().min(1).max(100),
    removeReasonPlaceholder: z.string().trim().min(1).max(180),
    savingLabel: z.string().trim().min(1).max(100),
    loadingLabel: z.string().trim().min(1).max(100),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
});
export const tasksDetailCopySetting = definePlatformSetting({
    definition: {
        key: 'tasks.copy.detail',
        moduleId: 'tasks',
        title: 'Task detail copy',
        helpText: 'Controls tenant-editable labels for task detail, checklist, link, comment, and template surfaces.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            backToTasksAction: 'Back to Tasks',
            descriptionLabel: 'Description',
            projectLabel: 'Project',
            assigneesLabel: 'Assignees',
            statusLabel: 'Status',
            priorityLabel: 'Priority',
            dueLabel: 'Due',
            noDueLabel: 'No due date',
            commentsTitle: 'Comments',
            commentPlaceholder: 'Add a working note',
            addCommentAction: 'Add comment',
            checklistTitle: 'Checklist',
            checklistProgressLabel: 'required items complete',
            checklistEmpty: 'No checklist items are attached to this task.',
            checklistItemLabel: 'Checklist item',
            checklistRequiredLabel: 'Required',
            addChecklistAction: 'Add checklist item',
            completeChecklistAction: 'Complete item',
            reopenChecklistAction: 'Reopen item',
            removeChecklistAction: 'Remove item',
            linksTitle: 'Links',
            linksEmpty: 'No links are attached to this task.',
            linkLabel: 'Link label',
            linkHrefLabel: 'URL or CREST path',
            addLinkAction: 'Add link',
            removeLinkAction: 'Remove link',
            templatesTitle: 'Apply a task template',
            templateLabel: 'Template',
            applyTemplateAction: 'Apply template',
            noTemplatesLabel: 'No task templates are configured.',
            templateHistoryTitle: 'Applied templates',
            removeReasonPlaceholder: 'Explain why this item is being removed',
            savingLabel: 'Saving…',
            loadingLabel: 'Loading task detail…',
            loadError: 'Task detail could not be loaded.',
            mutationError: 'The task detail could not be changed. Refresh and try again.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: tasksDetailCopyValueSchema,
});
export const tasksMyDayCopyValueSchema = z.strictObject({
    panelTitle: z.string().trim().min(1).max(100),
    panelDescription: z.string().trim().min(1).max(300),
    overdueTitle: z.string().trim().min(1).max(100),
    todayTitle: z.string().trim().min(1).max(100),
    dueSoonTitle: z.string().trim().min(1).max(100),
    noDueTitle: z.string().trim().min(1).max(100),
    dueLabel: z.string().trim().min(1).max(80),
    openTaskAction: z.string().trim().min(1).max(100),
    openAllTasksAction: z.string().trim().min(1).max(100),
    emptyLabel: z.string().trim().min(1).max(200),
    loadingLabel: z.string().trim().min(1).max(100),
    loadError: z.string().trim().min(1).max(240),
});
export const tasksMyDayCopySetting = definePlatformSetting({
    definition: {
        key: 'tasks.copy.my-day',
        moduleId: 'tasks',
        title: 'Tasks My Day copy',
        helpText: 'Controls tenant-editable labels in the optional Tasks panel on the shared My Day host.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            panelTitle: 'My tasks',
            panelDescription: 'Open work assigned to you, grouped in the tenant time zone.',
            overdueTitle: 'Overdue',
            todayTitle: 'Due today',
            dueSoonTitle: 'Due soon',
            noDueTitle: 'No due date',
            dueLabel: 'Due',
            openTaskAction: 'Open task',
            openAllTasksAction: 'Open all Tasks',
            emptyLabel: 'No active tasks need your attention.',
            loadingLabel: 'Loading your tasks…',
            loadError: 'Your task summary could not be loaded.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: tasksMyDayCopyValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => {
                const source = typeof value === 'object' && value !== null && !Array.isArray(value)
                    ? value
                    : {};
                return { ...source, openAllTasksAction: 'Open all Tasks' };
            },
        }],
});
const tasksFocusLaneLabelsSchema = z.strictObject({
    inbox: z.string().trim().min(1).max(60),
    today: z.string().trim().min(1).max(60),
    doing: z.string().trim().min(1).max(60),
    waiting: z.string().trim().min(1).max(60),
    blocked: z.string().trim().min(1).max(60),
    done: z.string().trim().min(1).max(60),
});
const tasksFocusWindowStatusLabelsSchema = z.strictObject({
    planning: z.string().trim().min(1).max(60),
    active: z.string().trim().min(1).max(60),
    closed: z.string().trim().min(1).max(60),
});
export const tasksFocusCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(300),
    dailyLabel: z.string().trim().min(1).max(60),
    weeklyLabel: z.string().trim().min(1).max(60),
    createWindowAction: z.string().trim().min(1).max(100),
    closeWindowAction: z.string().trim().min(1).max(100),
    closeoutNotesLabel: z.string().trim().min(1).max(100),
    quickAddTitle: z.string().trim().min(1).max(100),
    templateLabel: z.string().trim().min(1).max(100),
    noTemplateLabel: z.string().trim().min(1).max(100),
    taskTitleLabel: z.string().trim().min(1).max(100),
    projectLabel: z.string().trim().min(1).max(100),
    personalTaskLabel: z.string().trim().min(1).max(100),
    laneLabel: z.string().trim().min(1).max(100),
    commitmentLabel: z.string().trim().min(1).max(100),
    priorityLabel: z.string().trim().min(1).max(100),
    estimatedMinutesLabel: z.string().trim().min(1).max(100),
    addTaskAction: z.string().trim().min(1).max(100),
    removeTaskAction: z.string().trim().min(1).max(100),
    completeTaskAction: z.string().trim().min(1).max(100),
    moveUpAction: z.string().trim().min(1).max(100),
    moveDownAction: z.string().trim().min(1).max(100),
    savingLabel: z.string().trim().min(1).max(100),
    loadingLabel: z.string().trim().min(1).max(100),
    emptyWindowTitle: z.string().trim().min(1).max(120),
    emptyWindowDescription: z.string().trim().min(1).max(300),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    signoffPendingLabel: z.string().trim().min(1).max(200),
    projectPanelTitle: z.string().trim().min(1).max(100),
    projectPanelDescription: z.string().trim().min(1).max(300),
    projectPanelEmpty: z.string().trim().min(1).max(200),
    openTasksAction: z.string().trim().min(1).max(100),
    dueLabel: z.string().trim().min(1).max(80),
    laneLabels: tasksFocusLaneLabelsSchema,
    windowStatusLabels: tasksFocusWindowStatusLabelsSchema,
});
export const tasksFocusCopySetting = definePlatformSetting({
    definition: {
        key: 'tasks.copy.focus',
        moduleId: 'tasks',
        title: 'Tasks focus copy',
        helpText: 'Controls tenant-editable labels for Focus planning and the Project task panel.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Focus',
            title: 'Focus',
            description: 'Plan daily or weekly commitments without bypassing the task sign-off lifecycle.',
            dailyLabel: 'Daily',
            weeklyLabel: 'Weekly',
            createWindowAction: 'Start focus window',
            closeWindowAction: 'Close focus window',
            closeoutNotesLabel: 'Closeout notes',
            quickAddTitle: 'Quick add',
            templateLabel: 'Template',
            noTemplateLabel: 'Start from scratch',
            taskTitleLabel: 'Task title',
            projectLabel: 'Project',
            personalTaskLabel: 'Personal / no Project',
            laneLabel: 'Lane',
            commitmentLabel: 'Commitment',
            priorityLabel: 'Priority',
            estimatedMinutesLabel: 'Estimated minutes',
            addTaskAction: 'Add to Focus',
            removeTaskAction: 'Remove from Focus',
            completeTaskAction: 'Complete / sign off',
            moveUpAction: 'Move up',
            moveDownAction: 'Move down',
            savingLabel: 'Saving…',
            loadingLabel: 'Loading Focus…',
            emptyWindowTitle: 'No Focus window yet',
            emptyWindowDescription: 'Start a daily or weekly window, then add the work that matters now.',
            loadError: 'Focus could not be loaded.',
            mutationError: 'Focus could not be changed. Refresh and try again.',
            signoffPendingLabel: 'Your sign-off was saved. Other required sign-offs are still pending.',
            projectPanelTitle: 'Project tasks',
            projectPanelDescription: 'Open Tasks work connected to this Project.',
            projectPanelEmpty: 'No visible open Tasks are connected to this Project.',
            openTasksAction: 'Open Tasks',
            dueLabel: 'Due',
            laneLabels: {
                inbox: 'Inbox', today: 'Today', doing: 'Doing', waiting: 'Waiting', blocked: 'Blocked', done: 'Done',
            },
            windowStatusLabels: { planning: 'Planning', active: 'Active', closed: 'Closed' },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: tasksFocusCopyValueSchema,
});
export const tasksNotificationConfigurationValueSchema = z.strictObject({
    assignmentIntentsEnabled: z.boolean(),
    dueSoonIntentsEnabled: z.boolean(),
    openDigestIntentsEnabled: z.boolean(),
    dueSoonWindowHours: z.number().int().min(1).max(336),
    maxTasksPerMessage: z.number().int().min(1).max(100),
});
export const tasksNotificationConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'tasks.notifications.configuration',
        moduleId: 'tasks',
        title: 'Tasks notification behavior',
        helpText: 'Controls provider-neutral assignment, due-soon, and open-task digest intent generation. Delivery remains owned by Communications.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            assignmentIntentsEnabled: true,
            dueSoonIntentsEnabled: true,
            openDigestIntentsEnabled: false,
            dueSoonWindowHours: 24,
            maxTasksPerMessage: 25,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: tasksNotificationConfigurationValueSchema,
});
const tasksNotificationFrequencyLabelsSchema = z.strictObject({
    off: z.string().trim().min(1).max(60),
    daily: z.string().trim().min(1).max(60),
    weekdays: z.string().trim().min(1).max(60),
    hourly: z.string().trim().min(1).max(60),
});
export const tasksNotificationCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(300),
    assignmentLabel: z.string().trim().min(1).max(120),
    assignmentHelp: z.string().trim().min(1).max(300),
    reminderLabel: z.string().trim().min(1).max(120),
    reminderHelp: z.string().trim().min(1).max(300),
    frequencyLabel: z.string().trim().min(1).max(100),
    timeLabel: z.string().trim().min(1).max(100),
    timeZoneLabel: z.string().trim().min(1).max(100),
    saveAction: z.string().trim().min(1).max(80),
    savingLabel: z.string().trim().min(1).max(80),
    savedLabel: z.string().trim().min(1).max(120),
    loadingLabel: z.string().trim().min(1).max(120),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    assignmentSubjectTemplate: z.string().trim().min(1).max(240),
    assignmentBodyTemplate: z.string().trim().min(1).max(2_000),
    dueSoonSubjectTemplate: z.string().trim().min(1).max(240),
    dueSoonBodyTemplate: z.string().trim().min(1).max(2_000),
    openDigestSubjectTemplate: z.string().trim().min(1).max(240),
    openDigestBodyTemplate: z.string().trim().min(1).max(2_000),
    frequencyLabels: tasksNotificationFrequencyLabelsSchema,
});
export const tasksNotificationCopySetting = definePlatformSetting({
    definition: {
        key: 'tasks.copy.notifications',
        moduleId: 'tasks',
        title: 'Tasks notification copy',
        helpText: 'Controls tenant-editable preference labels and versioned notification templates. Supported placeholders are resolved by the future Communications delivery adapter.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Task notifications',
            title: 'My task emails',
            description: 'Choose which task emails you want and when reminders should be prepared.',
            assignmentLabel: 'Assignment emails',
            assignmentHelp: 'Prepare an email when a task is newly assigned to you.',
            reminderLabel: 'Open-task reminders',
            reminderHelp: 'Prepare due-soon and enabled digest reminders on your schedule.',
            frequencyLabel: 'Reminder frequency',
            timeLabel: 'Reminder time',
            timeZoneLabel: 'Time zone',
            saveAction: 'Save preferences',
            savingLabel: 'Saving…',
            savedLabel: 'Preferences saved.',
            loadingLabel: 'Loading task email preferences…',
            loadError: 'Task email preferences could not be loaded.',
            mutationError: 'Task email preferences could not be saved. Refresh and try again.',
            assignmentSubjectTemplate: 'New task assignment: {{taskTitle}}',
            assignmentBodyTemplate: 'You were assigned {{taskTitle}}. Due: {{dueAt}}. Project: {{projectLabel}}.',
            dueSoonSubjectTemplate: 'Tasks due soon',
            dueSoonBodyTemplate: '{{taskCount}} task(s) are due soon. Open CREST to review them.',
            openDigestSubjectTemplate: 'Your open task summary',
            openDigestBodyTemplate: 'You have {{taskCount}} open task(s). Open CREST to review and prioritize them.',
            frequencyLabels: {
                off: 'Off', daily: 'Daily', weekdays: 'Weekdays', hourly: 'Hourly',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: tasksNotificationCopyValueSchema,
});
export const tasksSettingRegistrations = [
    tasksWorkQueueCopySetting,
    tasksWorkQueueDisplaySetting,
    tasksFocusConfigurationSetting,
    tasksFocusCopySetting,
    tasksDetailCopySetting,
    tasksMyDayCopySetting,
    tasksNotificationConfigurationSetting,
    tasksNotificationCopySetting,
];
//# sourceMappingURL=tasks-setting-definitions.js.map